import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useParams, NavLink } from 'react-router-dom';
import { useGetBannersPageQuery } from '../../redux/api/content.api';
import Loader from '../../components/common/loader';
import Page404 from '../page404';
import { getMediaPath } from '../../app/functions';
import ContentSection from '../../components/common/ContentSection';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';
import { BannerLinkType } from '../../app/enums';


const BannersPage = () => {
  const params = useParams();
  const theme = useTheme();
  const text = useSelector((store) => store.config.Translations);
  const [sections, setSections] = useState([]);
  const [banners, setBanners] = useState([]);
  const { data, isLoading, isError } = useGetBannersPageQuery({
    name: params.name
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isExternalLink = (link) => {
    return link?.linkType === BannerLinkType.EXTERNAL && !link?.link?.includes('hot.co.il');
  }

  
  const processBannerLink = (bannerLink) => {
    if (!bannerLink || !bannerLink?.link) return '#';
    
    if (bannerLink?.link?.startsWith('http') || bannerLink?.link?.startsWith('https')) {
      try {
        const url = new URL(bannerLink?.link);

        if(/\/b\/(\d+)/g.test(url.pathname)) {
          return `/הטבה/${url.pathname.split('/b/')[1]}`;
        }
        if (/^\/c\/(\d+)/g.test(url.pathname)) {
          return `/קטגוריה/${url.pathname.split('/c/')[1]}`;
        }
        return url.pathname || '#';
      } catch (e) {
        console.error('Invalid URL:', bannerLink?.link);
        return '#';
      }
    }
    
    return bannerLink?.link
  };


  useEffect(() => {
    if (data) {
      setSections(data.contentSections || []);
      setBanners(data.banners || []);
    }
  }, [data]);

  if (isLoading) return <Loader className="initial-loader" />;
  if (isError) return <Page404 />;

  const expiredMessage = data?.errorMessage?.dateExpiredMessage
    ? ReactHtmlParser(data.errorMessage.dateExpiredMessage)
    : text['benefit_page_expired_msg'];

  return (
    <div className='benefitPage'>
    <Box sx={{ py: 4 }}>
        {!data?.isValid && (
            <div className='expired-layer'>
                <h2>{expiredMessage}</h2>
            </div>
        )}
            <>
                {sections[0] && (
                    <ContentSection key={sections[0].id} section={sections[0]} />
                )}

                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1,
                    mt: 4,
                    width: '100%'
                }}>
                    {isMobile ? (
                        banners.map((banner) => (
                            <Box 
                                key={banner.id} 
                                sx={{ 
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'transparent',
                                    overflow: 'hidden',
                                    borderRadius: '8px',
                                }}
                            >
                                {!isExternalLink(banner) ? <NavLink 
                                    to={processBannerLink(banner)}
                                    style={{ width: '100%', height: '100%', display: 'block' }}
                                >
                                    <img 
                                        src={getMediaPath(banner?.mobileImage)}
                                        alt="Banner"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                            borderRadius: '8px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </NavLink> :
                                <img 
                                src={getMediaPath(banner?.mobileImage)}
                                onClick={() => window.open(banner?.link.trim(), '_blank')}
                                alt="Banner"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                            />}
                            </Box>
                        ))
                    ) : (
                        // Desktop: Show banners with empty slots to maintain 3-column grid
                        Array.from({ length: Math.ceil(banners.length / 3) * 3 }, (_, index) => {
                            const banner = banners[index];
                            return (
                                <Box 
                                    key={banner ? banner.id : `empty-${index}`} 
                                    sx={{ 
                                        width: '100%',
                                        aspectRatio: '58/25',
                                        maxHeight: '250px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                        overflow: 'hidden',
                                        borderRadius: '8px',
                                        p: 2
                                    }}
                                >                                   
                                    {(banner && !isExternalLink(banner)) ? (
                                        <NavLink 
                                            to={String(processBannerLink(banner))}
                                            style={{ width: '100%', height: '100%', display: 'block' }}
                                        >
                                            <img 
                                                src={getMediaPath(banner?.desktopImage)}
                                                alt="Banner"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </NavLink>
                                    ) : (banner && isExternalLink(banner) && (
                                        <img 
                                            src={getMediaPath(banner?.desktopImage)}
                                            onClick={() => window.open(banner?.link.trim(), '_blank')}
                                            alt="Banner"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                                borderRadius: '8px',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </Box>
                            );
                        })
                    )}
                </Box>

                {sections[1] && (
                    <Box key={sections[1].id}>
                        <ContentSection key={sections[1].id} section={sections[1]} />
                    </Box>
                )}
            </>
        
    </Box>
    </div>
  );
};

export default BannersPage;
